// Anything exported from this file is importable by other in-browser modules.
import { getDesignTokens } from "./styles/theme";
import { LicenseInfo } from "@mui/x-license";

import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";

import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import VerticalSplitIcon from "@mui/icons-material/VerticalSplit";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import SortIcon from "@mui/icons-material/Sort";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import ChatIcon from "@mui/icons-material/Chat";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";

import { ThemeOptions } from "@mui/material";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CropFreeSharpIcon from "@mui/icons-material/CropFreeSharp";
import CloseFullscreenSharpIcon from "@mui/icons-material/CloseFullscreenSharp";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

LicenseInfo.setLicenseKey(
  "e3f99f32d8029d2c8cbe4416c3408871Tz0xMDkxMjksRT0xNzcyODQxNTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI="
);

declare module "@mui/material" {
  export interface TypeText {
    primary: string;
    secondary: string;
    tertiary: string;
    divider: string;
    tabs: string;
    disabled: string;
  }
}

declare module "@mui/material/Typography" {
  // noinspection JSUnusedGlobalSymbols
  export interface TypographyPropsVariantOverrides {
    body3: true;
    detailsBarText: true;
    PageHeader: true;
    OptionName: true;
    RadioLabel: true;
    ErrorDialogTitle: true;
    ErrorDialogText: true;
    CodeBlock: true;
    displayLarge: true;
    displayMedium: true;
    displayMediumProminent: true;
    displaySmall: true;
    headlineLarge: true;
    headlineMedium: true;
    headlineSmall: true;
    titleLarge: true;
    titleMedium: true;
    titleSmall: true;
    labelLargeProminent: true;
    labelLargeMono: true;
    labelLarge: true;
    labelMediumProminent: true;
    labelMedium: true;
    labelSmall: true;
    bodyLarge: true;
    bodyMedium: true;
    bodySmall: true;
  }
}

declare module "@mui/material/styles" {
  // noinspection JSUnusedGlobalSymbols
  interface Palette {
    header: Palette["primary"]; // this goes away with old layout
    tertiary: Palette["primary"];
    accent: Palette["primary"];
    outline: string;
  }

  // noinspection JSUnusedGlobalSymbols
  interface PaletteOptions {
    header: PaletteOptions["primary"]; // this goes away with old layout
    tertiary?: PaletteOptions["primary"];
    accent?: PaletteOptions["primary"];
    outline?: string;
  }

  // noinspection JSUnusedGlobalSymbols
  interface BreakpointOverrides {
    collapseContextDrawer: true;
    collapseNavDrawer: true;
  }
}

const setTheme = (theme: string): void => {
  localStorage.setItem("theme", theme);
  window.dispatchEvent(new Event("themeChange"));
};

const getTheme = (): ThemeOptions => {
  const theme = localStorage.getItem("theme");
  if (theme === null) {
    return getDesignTokens("light");
  } else if (theme === "Auto") {
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return getDesignTokens("dark");
    } else {
      return getDesignTokens("light");
    }
  } else if (theme === "Dark") {
    return getDesignTokens("dark");
  } else {
    return getDesignTokens("light");
  }
};

export { setTheme, getTheme, getDesignTokens, parse, match };
export {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Avatar,
  AppBar,
  Badge,
  Box,
  Button,
  ButtonGroup,
  createTheme,
  Collapse,
  Drawer,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fab,
  Fade,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid as Grid6,
  Grid2,
  Icon,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  MobileStepper,
  Popper,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  styled,
  Tab,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TextField,
  ThemeProvider,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  Chip,
  LinearProgress,
  CircularProgress,
  FormLabel,
  Slider,
  Stack,
  Switch,
  FormGroup,
  TableFooter,
  Popover,
  Step,
  StepIconProps,
  StepLabel,
  Stepper,
  StepConnector,
  stepConnectorClasses,
  useMediaQuery,
  lighten,
  darken,
  AutocompleteInputChangeReason,
} from "@mui/material";

export { alpha } from "@mui/material/styles";

export { Grid } from "./components/GridCompatability/Grid";

export type { SelectChangeEvent, TextFieldProps, PopperProps, PaperProps } from "@mui/material";

export { detailsBarMaxWidth, ChatBubbleDiv, PageHeaderCalloutDiv } from "./styles/theme";

export { customBreakpoints, breakpointValues } from "./styles/breakpoints";

export type { IconProps } from "@mui/material/Icon";
export { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";

export { TabContext, TabList, TabPanel, useTabContext } from "@mui/lab";

export { DateRangePickerV2 as DateRangePicker } from "./components/DateRangePickerV2/DateRangePickerV2";

export { LabelValue } from "./components/LabelValue/LabelValue";
export { LabelValueForOverview } from "./components/LabelValue/LabelValueForOverview";
export { ClickableCard } from "./components/ClickableCard/ClickableCard";
export { ChipList } from "./components/ChipList/ChipList";
export { StatusChip, StatusChipProps } from "./components/StatusIndicators/StatusChip";
export { StatusChips } from "./components/StatusIndicators/StatusChips";
export { StatusButton, StatusButtonProps } from "./components/StatusIndicators/StatusButton";
export { StatusButtons } from "./components/StatusIndicators/StatusButtons";

export { AutocompleteSearchBox } from "./components/AutocompleteSearchBox/AutocompleteSearchBox";
export { AutocompleteMultiSelectSearchBox } from "./components/AutocompleteMultiSelectSearchBox/AutocompleteMultiSelectSearchBox";

export { BackendFailureAlert } from "./components/BackendFailureAlert/BackendFailureAlert";
export { PageHeaderIconButton } from "./components/PageHeader/PageHeaderIconButton";
export { TableRowsFromObject } from "./components/TableRowsFromObject/TableRowsFromObject";
export { TableRowsFromObjectCompare } from "./components/TableRowsFromObjectCompare/TableRowsFromObjectCompare";

export { ViewAndEditIconsForBubble } from "./components/ViewAndEditIconsForBubble/ViewAndEditIconsForBubble";

export { DatePicker } from "./components/DatePicker/DatePicker";

export {
  StaticDatePicker,
  DatePicker as DatePicker7,
  LocalizationProvider,
} from "@mui/x-date-pickers";

export { CustomIconsForBubble } from "./components/CustomIconsForBubble/CustomIconsForBubble";

export { PDFViewer } from "./components/PDFViewer/PDFViewer";

export type {
  DataGridProProps,
  GridCellParams,
  GridColDef,
  GridColTypeDef,
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowsProp,
  GridValueFormatter as GridValueFormatterParams,
  GridValueFormatter,
  GridSortModel,
  GridPinnedRowsProp,
  GridEventListener,
  GridRowId,
  GridRenderCellParams,
  GridCellModesModel,
  MuiEvent,
} from "@mui/x-data-grid-pro";

export { DataGrid7 } from "./components/DataGrid/DataGrid7";
export { DataGrid } from "./components/DataGrid/DataGrid";
export { GridToolbarFilterButton } from "./components/DataGrid/GridToolbarFilterButton";
export { useGridApiRef } from "./components/DataGrid/useGridApiRef";
export { useGridApiContext } from "./components/DataGrid/useGridApiContext";

export {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridFilterPanel,
  GridFilterOperator,
  GridFilterInputValueProps,
  GridFilterItem,
  GridOverlay,
  GridPreferencePanelsValue,
  GridPreferencesPanelApi,
  GridToolbar,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton as GridToolbarFilterButton7,
  GridToolbarQuickFilter,
  GridToolbarProps,
  GridFilterForm,
  GridFilterModel,
  useGridApiRef as useGridApiRef7,
  useGridApiContext as useGridApiContext7,
  GridCellModes,
  GridCellEditStopReasons,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid-pro";

export { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

export type { FilterMenuType } from "./components/FilterMenu/FilterMenu";
export { ExpandNode } from "./components/ExpandNode/ExpandNode";
export { CollapseNode } from "./components/CollapseNode/CollapseNode";
export { Alert } from "./components/Alert/Alert";
export { CardListPaper } from "./components/CardList/internals/CardListPaper";
export { ChatHistoryPaper } from "./components/ChatHistoryPaper/internals/ChatHistoryPaper";
export { ChatInputPaper } from "./components/ChatInputPaper/internals/ChatInputPaper";
export { ChatWindowPaper } from "./components/ChatWindowPaper/internals/ChatWindowPaper";
export { CodeBlock } from "./components/CodeBlock/CodeBlock";
export { formatlocalDateToMonthDayYear } from "./utils/DateUtils";
export { dateToYearMonthDay } from "./utils/DateUtils";
export { CurrencyFormat } from "./components/CurrencyFormat/CurrencyFormat";
export { FilterMenu } from "./components/FilterMenu/FilterMenu";
export { MessageCard } from "./components/MessageCard/MessageCard";
export { MistPaper } from "./components/MistPaper/internals/MistPaper";
export { NavRailButton } from "./components/NavRailButton/NavRailButton";
export { PlatformBase } from "./components/PlatformBase/PlatformBase";
export { PlatformPageDialog } from "./components/PlatformPageDialog/PlatformPageDialog";
export { StyledStepConnector } from "./components/Stepper/internals/StyledStepConnector";
export { StyledStepIcon } from "./components/Stepper/StyledStepIcon";
export { Snackbar } from "./components/SuccessSnackbar/SuccessSnackbar";
export { SuggestionButton } from "./components/SuggestionButton/internals/SuggestionButton";
export { SuggestionsExpansionLinkPaper } from "./components/SuggestionsExpansionLinkPaper/internals/SuggestionsExpansionLinkPaper";
export { SuggestionsPaper } from "./components/SuggestionsPaper/internals/SuggestionsPaper";
export { TableContainer } from "./components/TableContainer/TableContainer";
export { TableCellHighlighted } from "./components/TableCellHighlighted/TableCellHighlighted";
export { TableStripedContainer } from "./components/TableStripedContainer/TableStripedContainer";
export { WelcomeSectionPaper } from "./components/WelcomeSectionPaper/internals/WelcomeSectionPaper";
export { AlertStatusIcon } from "./components/AlertStatusIcon/AlertStatusIcon";

export { pxToRem } from "./utils/fontSizeUtility";
export { formatDate } from "./utils/DateUtils";
export { formatPercentage } from "./utils/NumberUtils";
export { handleNumericField } from "./utils/NumberUtils";
export { handleIntegerField } from "./utils/NumberUtils";
export { focusRing } from "./utils/Accessibility";
export { focusPagination } from "./utils/Accessibility";
export { focusMenu } from "./utils/Accessibility";
export { focusBorder } from "./utils/Accessibility";
export { TitleCard } from "./components/TitleCard/TitleCard";
export { TypingIndicator } from "./components/TypingIndicator/TypingIndicator";

export { PlatformDialog } from "./components/PlatformDialog/PlatformDialog";
export { Workspace } from "./components/Workspace/Workspace";
export { AssistantPaper } from "./components/AssistantPaper/AssistantPaper";
export { DetailBarPaper } from "./components/DetailBarPaper/DetailBarPaper";
export { PageHeaderPaper } from "./components/PageHeader/PageHeaderPaper";
export { TabPanelPaper } from "./components/TabPanelPaper/TabPanelPaper";
export { ChatBlockTimestampPaper } from "./components/ChatBlockTimestampPaper/ChatBlockTimestampPaper";
export { AvatarNav } from "./components/AvatarNav/AvatarNav";
export { ChevronCircleSolid } from "./components/ChevronCircleSolid/ChevronCircleSolid";

export { ControlBar } from "./components/ControlBar/ControlBar";

export { ExclamationIcon } from "./components/Icon/ExclamationIcon";

export { ActionBubble } from "./components/ActionBubble/ActionBubble";
export { ActionButton } from "./components/ActionBubble/ActionButton";

export {
  ExpandLessIcon,
  ExpandMoreIcon,
  ErrorIcon,
  WarningIcon,
  CheckCircleIcon,
  InfoIcon,
  CloseIcon,
  SendIcon,
  VerticalSplitIcon,
  DescriptionOutlinedIcon,
  ErrorOutlineIcon,
  InfoOutlinedIcon,
  WarningAmberOutlinedIcon,
  ArrowBackIcon,
  LogoutIcon,
  SettingsOutlinedIcon,
  ExitToAppIcon,
  KeyboardArrowDownOutlinedIcon,
  SortIcon,
  NavigateBefore,
  NavigateNext,
  SearchIcon,
  CheckBoxOutlineBlankIcon,
  CheckBoxIcon,
  ChatOutlinedIcon,
  ChatIcon,
  MenuIcon,
  MenuOpenIcon,
  KeyboardDoubleArrowUpIcon,
  KeyboardDoubleArrowDownIcon,
  KeyboardArrowUpIcon,
  KeyboardArrowDownIcon,
  CropFreeSharpIcon,
  CloseFullscreenSharpIcon,
};
